import { useEffect, useCallback, useRef } from "react";
import "../styles/skills.css";

import heroBg from "../photos/skills.jpg";

/* ── Programming Languages ── */
import hdlImg    from "../photos/skills_hdl.png";
import cImg      from "../photos/skills_c.png";
import pythonImg from "../photos/skills_python.png";
import htmlImg   from "../photos/skills_html.png";
import cssImg    from "../photos/skills_css.png";
import jsImg     from "../photos/skills_js.png";
import cppImg    from "../photos/skills_c++.png";
import javaImg   from "../photos/skills_java.png";

/* ── Web Development Tools ── */
import vscodeImg    from "../photos/skills_vscode.png";
import reactImg     from "../photos/skills_react.png";
import nodejsImg    from "../photos/skills_nodejs.png";
import cloudfareImg from "../photos/skills_cfare.png";

/* ── Software / Hardware Tools ── */
import quartusImg from "../photos/skills_quartus.png";
import xilinxImg  from "../photos/skills_xilinx.png";
import edaImg     from "../photos/skills_eda.png";
import devcImg    from "../photos/skills_devc.png";
import gcolabImg  from "../photos/skills_gcolab.png";
import altiumImg  from "../photos/skills_altium.png";
import ltspiceImg from "../photos/skills_ltspice.png";
import arduinoImg from "../photos/skills_arduino.png";
import wokwiImg   from "../photos/skills_wokwi.png";
import ciscoptImg from "../photos/skills_ciscopt.png";
import macroImg   from "../photos/skills_macro.png";

/* ── Other Skills ── */
import psolveImg   from "../photos/skills_psolve.png";
import leadImg     from "../photos/skills_lead.png";
import creativeImg from "../photos/skills_creative.png";
import wiringImg   from "../photos/skills_wiring.png";

/* ════════════════════════════════════════
   SECTION DATA
════════════════════════════════════════ */

const SECTIONS = [
  {
    id: "lang",
    label: "Programming Languages",
    scrollable: true,
    items: [
      { name: "Hardware Description Language", img: hdlImg },
      { name: "C",                             img: cImg },
      { name: "Python",                        img: pythonImg },
      { name: "HTML",                          img: htmlImg },
      { name: "CSS",                           img: cssImg },
      { name: "JavaScript",                    img: jsImg },
      { name: "C++",                           img: cppImg },
      { name: "Java",                          img: javaImg },
    ],
  },
  {
    id: "web",
    label: "Web Development Tools",
    scrollable: false,   /* only 2 items — static grid */
    items: [
      { name: "Visual Studio Code", img: vscodeImg },
      { name: "React",              img: reactImg },
      { name: "Node JS",            img: nodejsImg },
      { name: "Cloudfare",              img: cloudfareImg },
    ],
  },
  {
    id: "tools",
    label: "Software / Hardware Tools",
    scrollable: true,
    items: [
      { name: "Quartus",     img: quartusImg },
      { name: "Xilinx",      img: xilinxImg },
      { name: "EDA Playground", img: edaImg },
      { name: "Dev C++",     img: devcImg },
      { name: "Google Colab", img: gcolabImg },
      { name: "Altium Designer", img: altiumImg },
      { name: "LTSpice",     img: ltspiceImg },
      { name: "Arduino IDE", img: arduinoImg },
      { name: "Wokwi",       img: wokwiImg },
      { name: "Cisco Packet Tracer",   img: ciscoptImg },
      { name: "Excel Macro", img: macroImg },
    ],
  },
  {
    id: "other",
    label: "Other Skills",
    scrollable: false,
    items: [
      { name: "Problem Solving",   img: psolveImg },
      { name: "Team Leading",      img: leadImg },
      { name: "Creative Thinking", img: creativeImg },
      { name: "Wiring",            img: wiringImg },
    ],
  },
];

/* ════════════════════════════════════════
   REVEAL HOOK
════════════════════════════════════════ */

function useReveal() {
  useEffect(() => {
    const nodes = document.querySelectorAll(".skl-reveal");
    const obs = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            obs.unobserve(e.target);
          }
        }),
      { root: null, threshold: 0.06, rootMargin: "0px 0px -16px 0px" }
    );
    nodes.forEach((n) => obs.observe(n));
    return () => obs.disconnect();
  }, []);
}

/* ════════════════════════════════════════
   DRAG-SCROLL HOOK
════════════════════════════════════════ */

function useDragScroll(ref) {
  const dragging = useRef(false);
  const startX   = useRef(0);
  const scrollL  = useRef(0);

  const onDown = useCallback((e) => {
    dragging.current = true;
    startX.current   = e.pageX - ref.current.offsetLeft;
    scrollL.current  = ref.current.scrollLeft;
    ref.current.classList.add("is-dragging");
  }, [ref]);

  const onUp = useCallback(() => {
    dragging.current = false;
    ref.current?.classList.remove("is-dragging");
  }, [ref]);

  const onMove = useCallback((e) => {
    if (!dragging.current) return;
    e.preventDefault();
    const x = e.pageX - ref.current.offsetLeft;
    ref.current.scrollLeft = scrollL.current - (x - startX.current) * 1.4;
  }, [ref]);

  /* wheel → horizontal */
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onWheel = (e) => { e.preventDefault(); el.scrollLeft += e.deltaY * 1.1; };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [ref]);

  return { onMouseDown: onDown, onMouseLeave: onUp, onMouseUp: onUp, onMouseMove: onMove };
}

/* ════════════════════════════════════════
   SKILL CARD
════════════════════════════════════════ */

function SkillCard({ item }) {
  return (
    <div className="skl-card">
      <div className="skl-card__img-wrap">
        <img src={item.img} alt={item.name} className="skl-card__img" draggable={false} />
      </div>
      <p className="skl-card__name">{item.name}</p>
    </div>
  );
}

/* ════════════════════════════════════════
   SCROLLABLE ROW
════════════════════════════════════════ */

function ScrollRow({ items }) {
  const ref = useRef(null);
  const handlers = useDragScroll(ref);

  return (
    <div className="skl-scroll" ref={ref} {...handlers}>
      <div className="skl-track">
        {items.map((item) => (
          <SkillCard key={item.name} item={item} />
        ))}
      </div>
    </div>
  );
}

/* ════════════════════════════════════════
   STATIC GRID ROW (non-scrollable)
════════════════════════════════════════ */

function StaticRow({ items }) {
  return (
    <div className="skl-static">
      {items.map((item) => (
        <SkillCard key={item.name} item={item} />
      ))}
    </div>
  );
}

/* ════════════════════════════════════════
   SKILL SECTION
════════════════════════════════════════ */

function SkillSection({ section, index }) {
  return (
    <div
      className="skl-section skl-reveal"
      style={{ transitionDelay: `${index * 0.08}s` }}
    >
      <div className="skl-section__header">
        <h2 className="skl-section__title">{section.label}</h2>
        {section.scrollable && (
          <span className="skl-section__hint">scroll or drag →</span>
        )}
      </div>

      {section.scrollable
        ? <ScrollRow items={section.items} />
        : <StaticRow items={section.items} />
      }
    </div>
  );
}

/* ════════════════════════════════════════
   MAIN
════════════════════════════════════════ */

export default function Skills() {
  useReveal();

  return (
    <div className="skills-page">

      {/* ── Hero ── */}
      <div className="skl-hero skl-reveal">
        <img src={heroBg} alt="" className="skl-hero__bg" aria-hidden="true" />
        <div className="skl-hero__overlay" />
        <div className="skl-hero__content">
          <h1 className="skl-hero__title">Technologies · Tools · Abilities</h1>
          <p className="skl-hero__sub">
            A growing toolkit built through years of coursework, projects, and hands-on tinkering —
            from low-level hardware to high-level web.
          </p>
        </div>
      </div>

      {/* Thin rule below hero */}
      <div className="skl-hero-rule skl-reveal" />

      {/* ── Skill sections ── */}
      <div className="skl-sections">
        {SECTIONS.map((s, i) => (
          <SkillSection key={s.id} section={s} index={i} />
        ))}
      </div>

    </div>
  );
}